import { jsx as _jsx } from "react/jsx-runtime";
import { compact } from 'lodash-es';
import { InvoiceDisplayStatus, InvoicePaymentDisplayStatusType } from 'anchor-scout-common/types';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import { SearchFilterType } from '~/components/TableFilters/types';
import { DateFilterPicker } from './DateFilterPicker';
export var FilterType = /*#__PURE__*/ function(FilterType) {
    FilterType["InvoiceStatus"] = "invoiceStatus";
    FilterType["PaymentStatus"] = "paymentStatus";
    FilterType["Date"] = "date";
    FilterType["QboSync"] = "qboSync";
    FilterType["XeroSync"] = "xeroSync";
    return FilterType;
}({});
export const tableFilterLabels = {
    ["invoiceStatus"]: 'Invoice status',
    ["paymentStatus"]: 'Payment status',
    ["date"]: 'Invoice issue date',
    ["qboSync"]: 'QuickBooks Online sync status',
    ["xeroSync"]: 'Xero sync status'
};
export const tableFilterGroups = [
    {
        label: 'Invoice',
        filters: [
            "invoiceStatus",
            "paymentStatus",
            "date"
        ]
    },
    {
        label: 'Accounting',
        filters: [
            "qboSync",
            "xeroSync"
        ]
    }
];
export function getActiveFilterGroups(params) {
    const accountingFilters = compact([
        params.showQboIntegration && params.hasQboFilterOptions && "qboSync",
        params.xeroIntegrationEnabled && params.showXeroIntegration && params.hasXeroFilterOptions && "xeroSync"
    ]);
    return compact([
        {
            label: 'Invoice',
            filters: [
                "invoiceStatus",
                "paymentStatus",
                "date"
            ]
        },
        accountingFilters.length > 0 && {
            label: 'Accounting',
            filters: accountingFilters
        }
    ]);
}
const invoiceDisplayStatusToLabel = {
    [InvoiceDisplayStatus.Issued]: 'Issued',
    [InvoiceDisplayStatus.Overdue]: 'Overdue',
    [InvoiceDisplayStatus.PaymentInitiated]: 'Processing',
    [InvoiceDisplayStatus.Paid]: 'Paid',
    [InvoiceDisplayStatus.Voided]: 'Voided',
    [InvoiceDisplayStatus.Refunded]: 'Refunded',
    [InvoiceDisplayStatus.RefundFailed]: 'Refund failed',
    [InvoiceDisplayStatus.PaymentDisputed]: 'Disputed',
    [InvoiceDisplayStatus.PartiallyRefunded]: 'Partially refunded'
};
const paymentDisplayStatusToLabel = {
    [InvoicePaymentDisplayStatusType.None]: 'None',
    [InvoicePaymentDisplayStatusType.Collected]: 'Collected',
    [InvoicePaymentDisplayStatusType.Paid]: 'Deposited',
    [InvoicePaymentDisplayStatusType.PaidExternally]: 'Paid externally',
    [InvoicePaymentDisplayStatusType.PaymentFailed]: 'Payment failed',
    [InvoicePaymentDisplayStatusType.PaymentMethodNotSet]: 'Payment method not set',
    [InvoicePaymentDisplayStatusType.PaymentMethodUnverified]: 'Payment method not verified',
    [InvoicePaymentDisplayStatusType.Pending]: 'Issued',
    [InvoicePaymentDisplayStatusType.Processing]: 'Payment initiated',
    [InvoicePaymentDisplayStatusType.Refunded]: 'Refund initiated',
    [InvoicePaymentDisplayStatusType.RefundFailed]: 'Refund failed',
    [InvoicePaymentDisplayStatusType.RequiresPaymentApproval]: 'Approval required',
    [InvoicePaymentDisplayStatusType.RequiresPaymentValidation]: 'Validation required',
    [InvoicePaymentDisplayStatusType.StoppedPayment]: 'Stopped payment',
    [InvoicePaymentDisplayStatusType.Disputed]: 'Disputed',
    [InvoicePaymentDisplayStatusType.RefundRequested]: 'Refund requested'
};
const datePresetToLabel = {
    'all-time': undefined,
    today: 'Today',
    'this-week': 'This week',
    'this-month': 'This month',
    'previous-month': 'Previous month',
    'last-three-months': 'Last 3 months'
};
const accountingStatusToLabel = {
    [AccountingStatusFilter.All]: undefined,
    [AccountingStatusFilter.Synced]: 'Synced',
    [AccountingStatusFilter.NotSynced]: 'Not synced'
};
function InvoiceDateFilter(param) {
    let { value, onChange } = param;
    return /*#__PURE__*/ _jsx(DateFilterPicker, {
        value: value,
        onChange: onChange
    });
}
export function getTableFilterConfig(params) {
    return {
        ["invoiceStatus"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: false,
            options: params.invoiceStatusFilterOptions.map((o)=>({
                    value: o.value,
                    label: o.label,
                    counter: o.counter
                }))
        },
        ["paymentStatus"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: false,
            options: params.paymentStatusFilterOptions.map((o)=>({
                    value: o.value,
                    label: o.label,
                    counter: o.counter
                }))
        },
        ["date"]: {
            type: SearchFilterType.CustomFilter,
            removable: false,
            component: InvoiceDateFilter,
            initialValue: 'all-time'
        },
        ["qboSync"]: {
            type: SearchFilterType.RadioFilter,
            removable: false,
            options: params.qboStatusFilterOptions.map((o)=>({
                    value: o.value,
                    label: o.label,
                    sublabel: "(".concat(o.counter, ")")
                })),
            defaultOption: AccountingStatusFilter.All
        },
        ["xeroSync"]: {
            type: SearchFilterType.RadioFilter,
            removable: false,
            options: params.xeroStatusFilterOptions.map((o)=>({
                    value: o.value,
                    label: o.label,
                    sublabel: "(".concat(o.counter, ")")
                })),
            defaultOption: AccountingStatusFilter.All
        }
    };
}
export function formatFilterDescription(filterType, value) {
    switch(filterType){
        case "invoiceStatus":
            {
                const statuses = value;
                if (!statuses.length) return undefined;
                return statuses.map((s)=>invoiceDisplayStatusToLabel[s]).join(', ');
            }
        case "paymentStatus":
            {
                const statuses = value;
                if (!statuses.length) return undefined;
                return statuses.map((s)=>paymentDisplayStatusToLabel[s]).join(', ');
            }
        case "date":
            {
                return datePresetToLabel[value];
            }
        case "qboSync":
        case "xeroSync":
            {
                return accountingStatusToLabel[value];
            }
    }
}
